import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';
import { Globe } from 'lucide-react';
import { changeLanguage, availableLanguages, toBaseLanguage } from '@/i18n';

/**
 * Language selector dropdown component
 * Can be used in both public menu and admin dashboard
 */
export function LanguageSelector({ variant = 'default', className = '' }) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const currentBaseLang = toBaseLanguage(i18n.resolvedLanguage || i18n.language);

  const currentLang = availableLanguages.find(l => l.code === currentBaseLang) || availableLanguages.find(l => l.code === 'en');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const visibleLanguages = availableLanguages.filter(l => !l.hidden);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const estimatedHeight = visibleLanguages.length * 44 + 16;
      setOpenUpward(spaceBelow < estimatedHeight);
    }
    setIsOpen(prev => !prev);
  };

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsOpen(false);
  };

  const dropdownPosition = openUpward
    ? 'bottom-full mb-2'
    : 'top-full mt-2';

  if (variant === 'minimal') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          ref={buttonRef}
          onClick={handleToggle}
          className="flex items-center gap-1 text-sm opacity-70 hover:opacity-100 transition"
          data-testid="language-selector-minimal"
        >
          <span className="text-base">{currentLang.flag}</span>
          <span className="hidden sm:inline">{currentLang.code.toUpperCase()}</span>
        </button>

        {isOpen && (
          <div className={`absolute right-0 ${dropdownPosition} bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 min-w-[120px] z-[200]`}>
            {visibleLanguages.map(lang => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition ${
                  lang.code === currentBaseLang ? 'bg-gray-50 dark:bg-gray-700 font-medium' : ''
                }`}
                data-testid={`language-option-${lang.code}`}
              >
                <span className="text-base">{lang.flag}</span>
                <span>{lang.nativeName}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default variant - full button with icon
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary hover:bg-secondary/80 transition text-sm w-full"
        data-testid="language-selector"
      >
        <Globe className="w-4 h-4" />
        <span className="text-base">{currentLang.flag}</span>
        <span>{currentLang.nativeName}</span>
      </button>

      {isOpen && (
        <div className={`absolute left-0 right-0 ${dropdownPosition} bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 min-w-[160px] z-[200]`}>
          {visibleLanguages.map(lang => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition ${
                lang.code === currentBaseLang ? 'bg-gray-50 dark:bg-gray-700 font-medium' : ''
              }`}
              data-testid={`language-option-${lang.code}`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span>{lang.nativeName}</span>
              {lang.code === currentBaseLang && (
                <span className="ml-auto text-green-500">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Simple language toggle for mobile/compact views
 */
export function LanguageToggle({ className = '' }) {
  const { i18n } = useTranslation();
  const currentBaseLang = toBaseLanguage(i18n.resolvedLanguage || i18n.language);
  
  const cycleLanguage = () => {
    const currentIndex = availableLanguages.findIndex(l => l.code === currentBaseLang);
    const nextIndex = (currentIndex + 1) % availableLanguages.length;
    changeLanguage(availableLanguages[nextIndex].code);
  };
  
  const currentLang = availableLanguages.find(l => l.code === currentBaseLang) || availableLanguages.find(l => l.code === 'en');
  
  return (
    <button
      onClick={cycleLanguage}
      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium opacity-70 hover:opacity-100 transition ${className}`}
      data-testid="language-toggle"
      title="Change language"
    >
      <span>{currentLang.flag}</span>
      <span>{currentLang.code.toUpperCase()}</span>
    </button>
  );
}

export default LanguageSelector;
